import FinancialAmount from "@/pages/(app)/returns/view/components/financial-card/financial-amount";
import FinancialSection from "@/pages/(app)/returns/view/components/financial-card/financial-section";
import { currencyFromISO } from "@/utils/currency";
import {
  getFinancialChargesList,
  getTotalDiscountHandled,
  getTotalDiscountToHandle,
  getTotalRefunded,
  getTotalToRefund,
} from "@/utils/return-requests";
import type {
  ReturnRequestDto,
  ShopDto,
} from "@babackapp/private-api-client/types";
import useTranslation from "@babackapp/translations/use-translation";

interface FinancialCardProps {
  returnRequest: ReturnRequestDto;
  shop: ShopDto;
}

export default function FinancialCard(props: FinancialCardProps) {
  const t = useTranslation();

  const allAmounts = getAllAmounts(props.returnRequest, props.shop);

  const shouldRenderReshippingExecuted = allAmounts.reshippingAmount > 0;
  const shouldRenderTotalToHandle =
    allAmounts.totalToHandle > 0 ||
    (!props.returnRequest.status.partiallyTreatedMerchant &&
      !props.returnRequest.status.treatedMerchant.done);
  const shouldRenderTotalTreated =
    allAmounts.totalTreated ||
    props.returnRequest.status.partiallyTreatedMerchant.done ||
    props.returnRequest.status.treatedMerchant.done;
  const shouldRenderTotalToRefund = allAmounts.totalToRefund > 0;
  const shouldRenderTotalToDiscount =
    allAmounts.discountAmountToHandleWithoutCharge > 0;
  const shouldRenderTotalRefunded = allAmounts.totalRefunded > 0;

  const discountCodes =
    props.returnRequest.financial_informations.discount.codes
      .map((code) => code.code)
      .join(", ");

  return (
    <div className="mb-6">
      <div className="space-y-6 divide-y divide-gray-200">
        {shouldRenderReshippingExecuted && (
          <FinancialSection title={t("returnDetail.reshippingExecuted")}>
            <FinancialAmount
              returnRequest={props.returnRequest}
              totalWithoutCharges={allAmounts.reshippingAmount}
              total={allAmounts.reshippingAmount}
              charges={[]}
            />
          </FinancialSection>
        )}
        {shouldRenderTotalToHandle && (
          <div className="space-y-4 pt-6">
            {shouldRenderTotalToRefund && (
              <FinancialSection title={t("returnDetail.remainingToRefund")}>
                <FinancialAmount
                  returnRequest={props.returnRequest}
                  totalWithoutCharges={allAmounts.totalToRefundWithoutCharges}
                  total={allAmounts.totalToRefund}
                  charges={allAmounts.chargesToRefund}
                />
              </FinancialSection>
            )}

            {shouldRenderTotalToDiscount && (
              <FinancialSection title={t("returnDetail.remainingToDiscount")}>
                <FinancialAmount
                  returnRequest={props.returnRequest}
                  totalWithoutCharges={
                    allAmounts.discountAmountToHandleWithoutCharge
                  }
                  total={allAmounts.discountAmountToHandle}
                  charges={allAmounts.chargesToDiscount}
                />
              </FinancialSection>
            )}
            <div className="flex text-base font-medium items-center justify-between">
              <div className="">{t("returnDetail.remainingToHandle")}</div>
              <div id="totalTohandle" className="">
                {allAmounts.totalToHandle.toFixed(2)}{" "}
                {currencyFromISO(props.returnRequest.currency.customerCurrency)}
              </div>
            </div>
          </div>
        )}
        {shouldRenderTotalTreated && (
          <div className="space-y-4 pt-6">
            {shouldRenderTotalRefunded && (
              <FinancialSection title={t("returnDetail.refundExecuted")}>
                <FinancialAmount
                  returnRequest={props.returnRequest}
                  totalWithoutCharges={allAmounts.totalRefundedWithoutCharges}
                  total={allAmounts.totalRefunded}
                  charges={allAmounts.chargesRefunded}
                />
              </FinancialSection>
            )}
            {allAmounts.discountAmountHandled > 0 && (
              <FinancialSection
                title={
                  t("returnDetail.discountExecuted") + `: ${discountCodes}`
                }
              >
                <FinancialAmount
                  returnRequest={props.returnRequest}
                  totalWithoutCharges={
                    allAmounts.discountAmountHandledWithoutCharges
                  }
                  total={allAmounts.discountAmountHandled}
                  charges={allAmounts.chargesDiscounted}
                />
              </FinancialSection>
            )}
            <div className="flex items-center justify-between">
              <div className="text-base font-medium">
                {t("returnDetail.totalTreated")}
              </div>
              <div
                id="totalTreated"
                className="text-base font-medium text-gray-900"
              >
                {allAmounts.totalTreated.toFixed(2)}{" "}
                {currencyFromISO(props.returnRequest.currency.customerCurrency)}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function getAllAmounts(returnRequest: ReturnRequestDto, shop: ShopDto) {
  const totalToRefund = getTotalToRefund(returnRequest, {
    withCharges: true,
    notValidatedByLogistician: !shop.workflowLogistician.activated,
  });
  const discountAmountToHandleWithoutCharge = getTotalDiscountToHandle(
    returnRequest,
    {
      withCharges: false,
      withBonus: false,
      notValidatedByLogistician: !shop.workflowLogistician.activated,
    },
  );
  const discountAmountHandled = getTotalDiscountHandled(returnRequest, {
    withCharges: true,
    withBonus: true,
    realDiscount: true,
  });
  const discountAmountHandledWithoutCharges = getTotalDiscountHandled(
    returnRequest,
    {
      withCharges: false,
      withBonus: false,
      realDiscount: false,
    },
  );
  const totalRefunded = getTotalRefunded(returnRequest, {
    withCharges: false,
    realRefund: true,
  });
  const totalRefundedWithoutCharges = getTotalRefunded(returnRequest, {
    withCharges: false,
  });

  const totalToHandle = totalToRefund + discountAmountToHandleWithoutCharge;
  const totalTreated = totalRefunded + discountAmountHandled;
  return {
    reshippingAmount:
      returnRequest.financial_informations.reshipping.presentmentAmount ||
      returnRequest.financial_informations.reshipping.amount,
    discountAmountToHandle: getTotalDiscountToHandle(returnRequest, {
      withBonus: true,
      withCharges: true,
      notValidatedByLogistician: !shop.workflowLogistician.activated,
    }),
    discountAmountToHandleWithoutCharge,
    discountAmountHandled,
    discountAmountHandledWithoutCharges,
    totalRefunded,
    totalRefundedWithoutCharges,
    totalToRefund,
    totalToRefundWithoutCharges: getTotalToRefund(returnRequest, {
      withCharges: false,
      notValidatedByLogistician: !shop.workflowLogistician.activated,
    }),
    totalToHandle,
    totalTreated,
    chargesRefunded: getFinancialChargesList(returnRequest, "refund", true),
    chargesToRefund: getFinancialChargesList(returnRequest, "refund", false),
    chargesToDiscount: getFinancialChargesList(
      returnRequest,
      "discount",
      false,
      { notValidatedByLogistician: !shop.workflowLogistician.activated },
    ),
    chargesDiscounted: getFinancialChargesList(returnRequest, "discount", true),
  };
}
